// ABCRPT1 (docs/build-briefs/ABCRPT1-abc-spirits-used-vs-sold.md): the body of the Spirits used vs sold (ABC) report, drawn
// inside the Reports frame (ui/src/reports.ts owns the title, the Export menu and the loading and error states). It draws
// what the server sent through the rules in abcSpiritsModel.ts. Help is a hover on the heading it explains (HELPHOVER1),
// never an info icon. A missing figure is a dash whose hover is the plain reason.

import type { AbcFigureDto, AbcSizeRowDto, AbcSpiritsReportDto, AbcSpiritsRowDto } from "./api.ts";
import {
  agreementText,
  bottleCell,
  canShowNextMonth,
  missingSummary,
  monthLabel,
  monthStatus,
  sizeText,
} from "./abcSpiritsModel.ts";
import { bindHoverTip } from "./hoverHelp.ts";
import { formatMoney } from "./reportFormat.ts";

export interface AbcSpiritsHandlers {
  onPrevious: () => void;
  onNext: () => void;
}

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) element.setAttribute(key, value);
  for (const child of children) element.append(child);
  return element;
}

function clear(element: HTMLElement): void {
  while (element.firstChild !== null) element.removeChild(element.firstChild);
}

function figureTd(figure: AbcFigureDto, extraClass = ""): HTMLElement {
  const cell = bottleCell(figure);
  const td = h("td", {
    cls: `${extraClass}${cell.reason === null ? "" : " zero"}`.trim(),
    text: cell.text,
  });
  if (cell.reason !== null) bindHoverTip(td, cell.reason);
  return td;
}

function headerCell(label: string, help: string, left = false): HTMLElement {
  const th = h("th", { cls: left ? "l" : "", text: label });
  bindHoverTip(th, help);
  return th;
}

const HELP = {
  brand: "The item's Producer where one is set, otherwise the item's name.",
  item: "The item's own name.",
  size: "The bottle size recorded on the item. Items with no size are grouped together.",
  beginning:
    "Bottles on hand at the close of the day before the month began. Blank when the item was not counted in the days before.",
  purchased: "Bottles received during the month, including delivery corrections.",
  usedSold: "What the recipes and pours on drinks you marked Prepared took out of the bottle.",
  lost: "Bottles written off as Breakage or Lost or stolen. Spills, over-pours and tasting are shown separately.",
  other:
    "Spills, over-pours, tasting and spoiled stock. They are real use that no drink sold accounts for.",
  ending:
    "Bottles on hand on the last day. Blank when the item was not counted in the days before.",
  usedCounted:
    "Beginning plus purchased, minus lost or broken and ending. This is how the board works out containers used.",
  difference:
    "Counted use minus the use drinks sold account for. A positive number means more left the bottle than was sold.",
  agreement:
    "Agrees when counted use and the use drinks sold account for are within about one pour of each other.",
} as const;

function rowTr(row: AbcSpiritsRowDto): HTMLElement {
  const tr = h("tr", { testid: `abc-row-${row.liquorProductId}` });
  tr.append(
    h("td", { cls: "l abc-brand", text: row.brand }),
    h("td", { cls: "l", text: row.itemName }),
    h("td", { cls: "l", text: sizeText(row.sizeLabel) }),
    figureTd(row.beginning),
    figureTd(row.purchased),
    figureTd(row.usedComputed),
    figureTd(row.lostOrBroken),
    figureTd(row.otherWaste),
    figureTd(row.ending),
    figureTd(row.usedCounted),
    figureTd(row.difference, "strong"),
    h("td", { cls: "l", text: agreementText(row.agreement) }),
  );
  return tr;
}

function sizeTr(size: AbcSizeRowDto): HTMLElement {
  const tr = h("tr", { testid: "abc-size-row" });
  tr.append(
    h("td", { cls: "l", text: sizeText(size.sizeLabel) }),
    h("td", { text: String(size.items) }),
    figureTd(size.beginning),
    figureTd(size.purchased),
    figureTd(size.usedCounted),
    figureTd(size.lostOrBroken),
    figureTd(size.ending),
    figureTd(size.usedComputed),
    figureTd(size.difference, "strong"),
    h("td", { cls: "l", text: agreementText(size.agreement) }),
  );
  return tr;
}

function tableWrap(label: string, head: HTMLElement[], rows: HTMLElement[]): HTMLElement {
  return h(
    "div",
    { cls: "stw-tblwrap hscroll", attrs: { tabindex: "0", role: "region", "aria-label": label } },
    [h("table", {}, [h("thead", {}, [h("tr", {}, head)]), h("tbody", {}, rows)])],
  );
}

function figureTile(
  label: string,
  help: string,
  value: string,
  note: string,
  testid: string,
): HTMLElement {
  const title = h("div", { cls: "stw-clabel", text: label });
  bindHoverTip(title, help);
  return h("div", { cls: "abc-tile", testid }, [
    title,
    h("div", { cls: "abc-tile-value", text: value }),
    h("div", { cls: "abc-tile-note", text: note }),
  ]);
}

export function renderAbcSpirits(
  root: HTMLElement,
  report: AbcSpiritsReportDto,
  handlers: AbcSpiritsHandlers,
): void {
  clear(root);
  const f = report.profile.fields;
  const container = h("div", { cls: "stw abc", testid: "abc-root" });

  const prev = h("button", {
    cls: "stw-arrow",
    text: "‹",
    testid: "abc-prev-month",
    attrs: { type: "button", "aria-label": "Previous month" },
  });
  prev.addEventListener("click", handlers.onPrevious);
  const next = h("button", {
    cls: "stw-arrow",
    text: "›",
    testid: "abc-next-month",
    attrs: { type: "button", "aria-label": "Next month" },
  });
  if (!canShowNextMonth(report.meta.month, report.meta.today)) next.setAttribute("disabled", "");
  next.addEventListener("click", handlers.onNext);
  container.append(
    h("div", { cls: "stw-controls" }, [
      h("div", { cls: "stw-ctl" }, [
        h("span", { cls: "stw-clabel", text: "Month" }),
        h("div", { cls: "stw-period" }, [
          prev,
          h("span", {
            cls: "stw-plabel",
            text: monthLabel(report.meta.month),
            testid: "abc-month-label",
          }),
          next,
        ]),
      ]),
    ]),
  );
  if (report.meta.partialMonth) {
    container.append(
      h("p", { cls: "stw-cnote", text: monthStatus(report), testid: "abc-partial" }),
    );
  }

  // The figures the state's Financial and Inventory Report asks for.
  const fig = report.figures;
  container.append(
    h("div", { cls: "abc-tiles" }, [
      figureTile(
        f.averageDrinkPrice,
        "Net drink sales for the month divided by the drinks sold.",
        fig.averageDrinkPriceCents === null ? "-" : formatMoney(fig.averageDrinkPriceCents),
        fig.averageDrinkPriceMissing ??
          `${formatMoney(fig.drinkSalesCents)} over ${fig.drinksSold} drinks`,
        "abc-avg-price",
      ),
      figureTile(
        f.averageShotSize,
        "Ounces poured from spirits bottles divided by the number of pours recorded when drinks were made.",
        fig.averageShotOunces === null ? "-" : `${Number(fig.averageShotOunces).toFixed(2)} oz`,
        fig.averageShotMissing ??
          `${Number(fig.pouredOunces).toFixed(1)} oz over ${fig.pours} pours`,
        "abc-avg-shot",
      ),
    ]),
  );

  const summary = missingSummary(report);
  if (summary !== null) {
    container.append(h("p", { cls: "stw-cnote", text: summary, testid: "abc-missing-summary" }));
  }

  if (report.rows.length === 0) {
    container.append(
      h("div", { cls: "empty", testid: "abc-empty" }, [
        h("h3", { text: "No spirits activity this month" }),
        h("p", {
          text: "Spirits you receive, pour or count will appear here for the month you pick.",
        }),
      ]),
    );
    root.append(container);
    return;
  }

  const brandTitle = h("h2", {}, ["By brand and size"]);
  bindHoverTip(
    brandTitle,
    "Each spirits item for the month, with the bottles used by counts set beside the bottles used by drinks sold.",
  );
  container.append(
    h("section", { cls: "stw-daily", testid: "abc-brand-table" }, [
      brandTitle,
      tableWrap(
        "By brand and size",
        [
          headerCell("Brand", HELP.brand, true),
          headerCell("Item", HELP.item, true),
          headerCell("Size", HELP.size, true),
          headerCell("Beginning", HELP.beginning),
          headerCell("Purchased", HELP.purchased),
          headerCell("Used (sold)", HELP.usedSold),
          headerCell("Lost or broken", HELP.lost),
          headerCell("Other waste", HELP.other),
          headerCell("Ending", HELP.ending),
          headerCell("Used (counted)", HELP.usedCounted),
          headerCell("Difference", HELP.difference),
          headerCell("Check", HELP.agreement, true),
        ],
        report.rows.map(rowTr),
      ),
    ]),
  );

  const formTitle = h("h2", {}, [`${report.profile.stateName} form by bottle size`]);
  bindHoverTip(
    formTitle,
    `The ${report.profile.formName} lines for each bottle size, in the board's words. The product does not file it for you.`,
  );
  container.append(
    h("section", { cls: "stw-daily", testid: "abc-size-table" }, [
      formTitle,
      tableWrap(
        `${report.profile.stateName} form by bottle size`,
        [
          headerCell("Size", HELP.size, true),
          headerCell("Items", "How many spirits items of this size are in the row."),
          headerCell(f.beginning, HELP.beginning),
          headerCell(f.purchased, HELP.purchased),
          headerCell(f.used, HELP.usedCounted),
          headerCell(f.lostStolenBroken, HELP.lost),
          headerCell(f.ending, HELP.ending),
          headerCell("Used (sold)", HELP.usedSold),
          headerCell("Difference", HELP.difference),
          headerCell("Check", HELP.agreement, true),
        ],
        report.sizes.map(sizeTr),
      ),
    ]),
  );

  const notes: string[] = [];
  if (report.notes.itemsWithoutSize > 0) {
    notes.push(
      `${report.notes.itemsWithoutSize} ${report.notes.itemsWithoutSize === 1 ? "item has" : "items have"} no bottle size, so its bottles cannot be shown.`,
    );
  }
  if (report.notes.mixedUnitItems.length > 0) {
    notes.push(
      `Left out because their stock is held in mixed units: ${report.notes.mixedUnitItems.join(", ")}.`,
    );
  }
  notes.push("Comped drinks are counted as sold.");
  notes.push(
    `Keep these records ${report.profile.retentionYears} years, apart from your other records.`,
  );
  container.append(h("p", { cls: "stw-foot", text: notes.join(" "), testid: "abc-foot" }));
  root.append(container);
}
